/* ════════════════════════════════════════════════════════════
   Coût de revient d'une livraison.

   Tout ce qui se règle est ici. Aucune de ces valeurs n'est
   écrite ailleurs dans le code.
   ════════════════════════════════════════════════════════════ */

export const LOGISTIQUE = {
  /* Points fixes, en coordonnées [longitude, latitude].

     Volontairement figés plutôt que géocodés à chaque calcul : un
     essai par le texte envoyait « Le Locle » en Normandie et la
     douane de Boncourt du mauvais côté de la frontière. Ces trois
     points ne bougeront jamais, autant les résoudre une fois. */
  depart: {
    label: 'Chalindrey',
    coords: [5.4355, 47.7995],
  },

  /* Bureaux de douane envisageables pour dédouaner.
     L'ordre n'a pas d'importance : le moins cher est mis en avant. */
  /* Coordonnées relevées sur les adresses exactes des postes, et non
     sur la commune voisine : le nom de ville seul décalait le point
     de plusieurs kilomètres, ce qui fausse d'autant le tronçon suisse
     et donc la RPLP. */
  douanes: [
    // 16A N 57, 25370 Jougne — relevé dans Plans
    { label: 'Vallorbe',       coords: [6.383870, 46.731846] },
    // D 461, 25130 Villers-le-Lac — relevé dans Plans
    { label: 'Le Locle',       coords: [6.710203, 47.052733] },
    // Plateforme Douanière 3, 2926 Boncourt (Suisse)
    { label: 'Delle/Boncourt', coords: [6.9861, 47.4928] },
  ],

  /* Profil de calcul d'itinéraire.
     'driving-car' suit les mêmes routes que Plans, ce qui permet de
     recouper les distances à la main. 'driving-hgv' écarte les voies
     interdites aux poids lourds : plus prudent, mais il allongeait
     Chalindrey → Jougne de 176 à 253 km, un détour que tu ne fais pas. */
  profil: 'driving-car',

  /* Critère d'optimisation.
     'shortest' privilégie le kilométrage, 'fastest' le temps de trajet.
     Par défaut le calcul prenait l'autoroute vers Vallorbe alors que la
     nationale est plus courte — et c'est celle qui est empruntée. */
  preference: 'shortest',

  /* Approvisionnement : achat de la marchandise et trajet pour aller
     la chercher. Le kilométrage est forfaitaire, il ne dépend pas du
     client livré. */
  /* Valeurs de repli quand aucun fournisseur n'est sélectionné. */
  prixDefaut: { paille: 100, foin: 100, luzerne: 100 },  // € la tonne
  kmApprovisionnement: 180,                              // km du trajet

  /* Taxe prélevée sur le montant facturé, par pays livré.

     Nulle en France : elle y est encaissée puis reversée à l'identique,
     donc sans effet comptable — l'inscrire des deux côtés n'apporterait
     qu'une ligne de plus à lire. */
  tauxTaxeVente: { CH: 2.6, FR: 0 },   // % du montant compta facturé

  /* Carburant */
  conso: 34,          // litres aux 100 km
  prixGasoil: 1.90,   // € HT le litre

  /* RPLP — redevance suisse sur le trafic des poids lourds.
     Formule officielle : poids × tarif × km / 100
     Le tarif est en centimes par tonne-kilomètre, d'où la division. */
  poidsRplp: 40,      // tonnes, poids total autorisé
  tarifRplp: 2.39,    // ct/tkm, catégorie Euro 6

  /* Le camion revient à vide : carburant et RPLP comptent double.
     La RPLP s'applique aussi aux courses à vide en Suisse. */
  allerRetour: true,
}

/* ── Calculs ─────────────────────────────────────────────────
   `kmFrance`  domicile → douane
   `kmSuisse`  douane → destination, seul tronçon soumis à la RPLP */

export function coutTrajet({ kmFrance, kmSuisse }, cfg = LOGISTIQUE, camions = 1) {
  const facteur = cfg.allerRetour ? 2 : 1
  /* Chaque camion refait le trajet en entier : deux marchandises qui
     ne tiennent pas dans une même remorque, c'est deux fois la route,
     deux fois le gasoil et deux fois la RPLP. */
  const n = Math.max(1, parseInt(camions, 10) || 1)
  /* Aller-retour complet : les deux tronçons, dans les deux sens.
     La RPLP garde sa propre assiette, le seul tronçon suisse. */
  const kmTotal = (kmFrance + kmSuisse) * facteur
  const kmRplp = kmSuisse * facteur

  const carburant = (kmTotal * cfg.conso / 100) * cfg.prixGasoil * n
  const rplp = ((cfg.poidsRplp * cfg.tarifRplp * kmRplp) / 100) * n

  return {
    kmFrance, kmSuisse,
    /* Kilométrages cumulés sur l'ensemble des camions. */
    kmTotal: kmTotal * n, kmRplp: kmRplp * n,
    camions: n,
    carburant,
    rplp,
    total: carburant + rplp,
  }
}

/* Nombre de camions déduit des lignes du devis : une marchandise ne se
   mélange pas à une autre dans la même remorque, chacune occupe donc
   au moins un camion. */
export function camionsDeLignes(lignes, capacite = 28) {
  const n = (lignes || [])
    .filter((l) => parseFloat(l.poids) > 0)
    .reduce((total, l) => total + Math.ceil(parseFloat(l.poids) / capacite), 0)
  return Math.max(1, n)
}

/* Famille tarifaire d'un produit. Même règle que les statistiques,
   pour que les deux racontent la même histoire. */
export function categorieProduit(nom) {
  const n = (nom || '').toLowerCase()
  if (n.includes('luzerne')) return 'luzerne'
  if (n.includes('foin') || n.includes('prairie')) return 'foin'
  return 'paille'
}

/* Achat de la marchandise, ligne par ligne, au prix de sa famille. */
export function coutMarchandise(lignes, prix) {
  return (lignes || []).reduce((total, l) => {
    const poids = parseFloat(l.poids) || 0
    const pu = parseFloat(prix?.[categorieProduit(l.produit)]) || 0
    return total + poids * pu
  }, 0)
}

/* Coût d'approvisionnement : la marchandise, plus le carburant du
   trajet pour aller la chercher. Indépendant de la douane retenue. */
export function coutApprovisionnement(km, cfg = LOGISTIQUE, camions = 1) {
  const distance = parseFloat(km)
  const reel = Number.isFinite(distance) ? distance : cfg.kmApprovisionnement
  /* Comme pour la livraison : chaque camion fait son propre voyage
     jusqu'au fournisseur. */
  const n = Math.max(1, parseInt(camions, 10) || 1)
  return {
    km: reel, camions: n, kmTotal: reel * n,
    carburant: (reel * cfg.conso / 100) * cfg.prixGasoil * n,
  }
}

/* Taxe fourrage : taxe douanière suisse sur le foin et la luzerne.
   Elle est refacturée au client, mais c'est bien nous qui la versons :
   elle appartient donc au coût de revient. Même formule que le devis,
   le tarif étant exprimé aux 100 kg. */
export function taxeFourrage(lignes) {
  return (lignes || []).reduce((total, l) => {
    const poids = parseFloat(l.poids) || 0
    const taux = parseFloat(l.taxe) || 0
    return total + taux * poids * 10
  }, 0)
}

/* Taux applicable au pays livré. */
export const tauxTaxe = (pays, cfg = LOGISTIQUE) =>
  cfg.tauxTaxeVente[pays] ?? cfg.tauxTaxeVente.CH

/* Taxe sur le montant facturé. */
export function taxeVente(montantFacture, pays = 'CH', cfg = LOGISTIQUE) {
  return ((parseFloat(montantFacture) || 0) * tauxTaxe(pays, cfg)) / 100
}

/* Itinéraires dans Plans. Apple attend latitude d'abord, soit
   l'inverse de nos coordonnées [longitude, latitude]. */
const pt = (c) => `${c[1]},${c[0]}`

/* Un seul tronçon. */
export function lienItineraire(de, vers) {
  if (!de || !vers) return null
  return `https://maps.apple.com/directions?source=${pt(de)}&destination=${pt(vers)}&mode=driving`
}

/* Trajet complet avec la douane en étape : le point d'entrée
   /directions accepte un waypoint, ce qui permet d'ouvrir d'un clic
   l'itinéraire réellement chiffré. */
export function lienItineraireComplet(de, etape, vers) {
  if (!de || !etape || !vers) return null
  return `https://maps.apple.com/directions?source=${pt(de)}`
    + `&waypoint=${pt(etape)}&destination=${pt(vers)}&mode=driving`
}

/* Reconstitue l'adresse de destination à partir des détails du devis. */
export function adresseDestination(details) {
  const trouve = (re) => details.find((d) => re.test(d.key))?.value?.trim()
  const parts = [
    trouve(/localit|adresse/i),
    trouve(/code\s*postal|\bnpa\b/i),
    trouve(/canton/i),
  ].filter(Boolean)
  return parts.length ? `${parts.join(', ')}, Suisse` : null
}
